/**
 * Autoplay Pilot
 * Deterministic-ish input generator for "watchable" gameplay feel testing.
 *
 * Enable with URL: `?autoplay=1`
 */

export class AutoplayPilot {
  constructor(options = {}) {
    this.startedAtMs = null;
    this.seed = Number(options.seed) || 0;
    this.mode = String(options.mode || "figure8");
    this._lastRollAt = -1e9;
    this._lastSpecialAt = -1e9;
    this._lastBombAt = -1e9;

    // Fire cadence state (tap-fire by default; occasional charge shots).
    this._fireMode = "tap"; // tap | charge_hold | charge_release
    this._chargeHoldUntil = -1e9;
    this._chargeReleaseUntil = -1e9;
    this._nextChargeAt = 3.8;
  }

  start(nowMs) {
    if (this.startedAtMs == null) this.startedAtMs = Number(nowMs) || 0;
  }

  getInput(nowMs, game = null) {
    const t = ((Number(nowMs) || 0) - (Number(this.startedAtMs) || 0)) / 1000;

    // Default: "watchable" demo mode input object.
    const input = {
      forward: false,
      backward: false,
      left: false,
      right: false,
      fire: false,
      boost: false,
      brake: false,
      bomb: false,
      roll: false,
      special: false,
    };

    // --- FIRE ---
    // Tap-fire so the ship actually shoots (hold-fire is charge mode now).
    // Occasionally do a charged shot by holding then releasing.
    if (this._fireMode === "tap") {
      const tapPeriod = 0.11;
      const tapDown = 0.065;
      input.fire = (t % tapPeriod) < tapDown;

      if (t >= this._nextChargeAt) {
        this._fireMode = "charge_hold";
        this._chargeHoldUntil = t + 0.82; // long enough to reach full-ish charge
      }
    } else if (this._fireMode === "charge_hold") {
      input.fire = true;
      if (t >= this._chargeHoldUntil) {
        this._fireMode = "charge_release";
        this._chargeReleaseUntil = t + 0.12;
      }
    } else {
      // charge_release
      input.fire = false;
      if (t >= this._chargeReleaseUntil) {
        this._fireMode = "tap";
        // Next charge after a while.
        this._nextChargeAt = t + 5.5 + ((this.seed * 0.17) % 1.4);
      }
    }

    // Movement: figure-8 / lanes. (Readable banking, not twitch spam.)
    const phase = t % 8;
    if (this.mode === "lanes") {
      // Snap between 5 "lanes" every second.
      const lane = Math.floor((t / 1.0) % 5);
      input.left = lane === 0 || lane === 1;
      input.right = lane === 3 || lane === 4;
      input.forward = lane === 0 || lane === 4;
      input.backward = lane === 2;
    } else {
      // Figure-8: left/right alternation + up/down alternation.
      input.left = phase < 2 || (phase >= 4 && phase < 6);
      input.right = (phase >= 2 && phase < 4) || (phase >= 6 && phase < 8);
      input.forward = phase < 4;
      input.backward = phase >= 4;
    }

    // --- SMART CORRECTIONS (optional, uses live game state) ---
    // Helps the demo survive to the boss and makes shots connect.
    if (game?.player?.object3D?.position && Array.isArray(game.enemies) && game.enemies.length > 0) {
      const px = Number(game.player.object3D.position.x) || 0;
      const py = Number(game.player.object3D.position.y) || 0;
      const pz = Number(game.player.object3D.position.z) || 0;

      const distSq = (ax, ay, az, bx, by, bz) => {
        const dx = ax - bx;
        const dy = ay - by;
        const dz = az - bz;
        return dx * dx + dy * dy + dz * dz;
      };

      // Avoid nearby enemy projectiles (biggest survival win).
      let nearestProj = null;
      let bestProjD2 = Infinity;
      if (Array.isArray(game.projectiles)) {
        for (const pr of game.projectiles) {
          if (!pr || pr.isPlayerProjectile) continue;
          const op = pr.object3D?.position;
          if (!op) continue;
          const d2 = distSq(Number(op.x) || 0, Number(op.y) || 0, Number(op.z) || 0, px, py, pz);
          if (d2 < bestProjD2) {
            bestProjD2 = d2;
            nearestProj = pr;
          }
        }
      }

      if (nearestProj && bestProjD2 < 150 * 150) {
        const op = nearestProj.object3D.position;
        const dx = (Number(op.x) || 0) - px;
        const dy = (Number(op.y) || 0) - py;
        // Jink away from the incoming bolt.
        const dead = 8;
        input.left = dx > dead;
        input.right = dx < -dead;
        input.forward = dy < -dead;
        input.backward = dy > dead;

        // Panic roll when very close.
        if (bestProjD2 < 80 * 80 && t - this._lastRollAt > 2.2) {
          this._lastRollAt = t;
          input.roll = true;
        }
      } else {
        // Aim toward the nearest enemy to shorten time-to-kill.
        let target = null;
        let bestD2 = Infinity;
        for (const e of game.enemies) {
          if (!e?.object3D?.position || e.isDestroyed) continue;
          const ep = e.object3D.position;
          const d2 = distSq(Number(ep.x) || 0, Number(ep.y) || 0, Number(ep.z) || 0, px, py, pz);
          if (d2 < bestD2) {
            bestD2 = d2;
            target = e;
          }
        }
        if (target?.object3D?.position) {
          const ep = target.object3D.position;
          const dx = (Number(ep.x) || 0) - px;
          const dy = (Number(ep.y) || 0) - py;
          const dead = 14;

          // Blend: keep the figure-8 vibe, but bias toward the target.
          if (Math.abs(dx) > dead) {
            input.left = dx < 0;
            input.right = dx > 0;
          } else {
            input.left = false;
            input.right = false;
          }
          if (Math.abs(dy) > dead) {
            input.forward = dy > 0;
            input.backward = dy < 0;
          } else {
            input.forward = false;
            input.backward = false;
          }
        }
      }

      // Avoid ramming the civilian freighter during the objective.
      if (Array.isArray(game.friendlies) && game.friendlies.length > 0) {
        let nearestF = null;
        let bestFD2 = Infinity;
        for (const f of game.friendlies) {
          if (!f?.object3D?.position || f.isDestroyed) continue;
          const fp = f.object3D.position;
          const d2 = distSq(Number(fp.x) || 0, Number(fp.y) || 0, Number(fp.z) || 0, px, py, pz);
          if (d2 < bestFD2) {
            bestFD2 = d2;
            nearestF = f;
          }
        }
        if (nearestF && bestFD2 < 140 * 140) {
          const fp = nearestF.object3D.position;
          const dx = (Number(fp.x) || 0) - px;
          const dy = (Number(fp.y) || 0) - py;
          const dead = 8;
          input.left = dx > dead;
          input.right = dx < -dead;
          input.forward = dy < -dead;
          input.backward = dy > dead;
          input.brake = true;
        }
      }
    }

    // Dramatic boost moments.
    const boostPulse = (t % 6.0);
    input.boost = boostPulse > 1.2 && boostPulse < 2.1;

    // Dramatic brake moments (short, punchy).
    const brakePulse = (t % 7.5);
    input.brake = brakePulse > 4.8 && brakePulse < 5.2;

    // Occasional barrel roll (edge-triggered).
    if (!input.roll && t - this._lastRollAt > 6.5) {
      this._lastRollAt = t;
      input.roll = true;
    }

    // Occasional special (edge-triggered).
    if (t - this._lastSpecialAt > 9.0) {
      this._lastSpecialAt = t;
      input.special = true;
    }

    // Rare bomb (edge-triggered).
    // In "smart" play, bombs help clear clutter before the boss.
    if (t > 10 && t - this._lastBombAt > 14.0) {
      this._lastBombAt = t;
      input.bomb = true;
    }

    return input;
  }
}
